import type { FC, MouseEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { ArrowRight, Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button, buttonVariants } from '@/components/ui/button'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'
import { MAIN_NAV } from '@/constants/navigation'

import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetTitle,
    SheetTrigger
} from '@/components/ui/sheet'

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [menuOpen, setMenuOpen] = useState(false)

    const isActive = (href: string): boolean => {
        if (href.includes('#')) return false
        if (href === '/') return router.pathname === '/'

        return router.pathname === href || router.pathname.startsWith(`${href}/`)
    }

    const handleMenuNavigate = (event: MouseEvent<HTMLAnchorElement>, href: string): void => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return

        event.preventDefault()
        setMenuOpen(false)

        window.setTimeout(() => {
            void router.push(href)
        }, 220)
    }

    return (
        <header
            id='site-header'
            className='sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-md'
        >
            <Container className='flex h-16 items-center justify-between gap-6 lg:h-20'>
                <Logo />

                <nav aria-label='Main' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {MAIN_NAV.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    aria-current={isActive(link.href) ? 'page' : undefined}
                                    className='rounded-full px-4 py-2 text-sm font-semibold text-foreground/75 transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring aria-[current=page]:bg-secondary aria-[current=page]:text-foreground'
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <Link
                        href='/contact'
                        className={cn(buttonVariants({ size: 'sm' }), 'hidden sm:inline-flex')}
                    >
                        Request a quote
                    </Link>

                    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                        <SheetTrigger
                            render={
                                <Button
                                    variant='outline'
                                    size='icon'
                                    className='lg:hidden'
                                />
                            }
                        >
                            <Menu className='size-5' />
                            <span className='sr-only'>
                                Open menu
                            </span>
                        </SheetTrigger>

                        <SheetContent side='right' className='w-full gap-0 p-0 sm:max-w-sm'>
                            <div className='border-b border-border px-6 py-5'>
                                <SheetTitle className='font-heading text-xl'>
                                    Menu
                                </SheetTitle>

                                <SheetDescription>
                                    Rice varieties, quality and quotes
                                </SheetDescription>
                            </div>

                            <nav aria-label='Mobile' className='px-3 py-4'>
                                <ul className='flex flex-col'>
                                    {MAIN_NAV.map(link => (
                                        <li key={link.href}>
                                            <Link
                                                href={link.href}
                                                onClick={event => handleMenuNavigate(event, link.href)}
                                                aria-current={isActive(link.href) ? 'page' : undefined}
                                                className='flex items-center justify-between rounded-xl px-3 py-3.5 text-base font-semibold text-foreground transition-colors hover:bg-secondary focus-visible:outline-2 focus-visible:outline-ring aria-[current=page]:bg-secondary'
                                            >
                                                {link.label}
                                                <ArrowRight className='size-4 text-muted-foreground' aria-hidden='true' />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </nav>

                            <div className='mt-auto border-t border-border p-6'>
                                <Link
                                    href='/contact'
                                    onClick={event => handleMenuNavigate(event, '/contact')}
                                    className={cn(buttonVariants({ size: 'lg' }), 'w-full')}
                                >
                                    Request a quote
                                </Link>

                                <p className='mt-3 text-center text-xs text-muted-foreground'>
                                    Specification, packing and price in one reply.
                                </p>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </Container>
        </header>
    )
}
